import React, { useState } from 'react';
import { z } from 'zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { AnyFieldApi } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { shouldShowFieldError, zodFieldValidator, PhoneSchema } from '@repo/validation';
import { toAppError } from '@repo/api-client';
import { getErrorMessage } from '@repo/i18n';
import { useCreateCustomer, checkDuplicateMobile } from '../hooks';
import { useCrmContact } from '../../crm/hooks';
import { Button } from '../../../components/ui/Button';
import { Input, FormField } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { Link } from '@tanstack/react-router';
import { useToast } from '../../../components/ui/Toast';
import { defineFormPage, useFormPage } from '@repo/form';
import { Banner } from '../../../components/ui/Banner.js';
import {
  DynamicContactList,
  DynamicAddressList,
  DynamicBankList,
  type ContactItem,
  type AddressItem,
  type BankItem,
} from '@repo/web-form';
import '../types';

const customerSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  fatherOrSpouse: z.string().optional(),
  dateOfBirth: z.string().optional().refine((val) => {
    if (!val) return true;
    const dob = new Date(val);
    const age = (new Date().getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    return age >= 18;
  }, 'Must be at least 18 years old'),
  gender: z.enum(['male', 'female', 'other', '']).optional(),
});

type FormValues = z.infer<typeof customerSchema>;

const customerFormConfig = defineFormPage<FormValues>({
  resource: 'customers',
  defaultValues: {
    name: '',
    fatherOrSpouse: '',
    dateOfBirth: '',
    gender: '' as never,
  },
  schema: customerSchema,
});

const fieldErr = zodFieldValidator;
const showErr = shouldShowFieldError;

export function CustomerCreateForm() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { contactId?: string };
  const contactId = search?.contactId;
  const crmContactQuery = useCrmContact(contactId ?? '');
  const crmContact = crmContactQuery.data;
  const queryClient = useQueryClient();

  const createCustomer = useCreateCustomer();
  const { toast } = useToast();
  const [duplicateWarning, setDuplicateWarning] = useState<{ exists: boolean; customerId: string | null } | null>(null);

  // Dynamic 1-to-many relationship states
  const [contacts, setContacts] = useState<ContactItem[]>([
    { type: 'PHONE', label: 'MOBILE', value: '', isPrimary: true, isWhatsapp: true },
  ]);

  const [addresses, setAddresses] = useState<AddressItem[]>([
    {
      addressType: 'RESIDENTIAL',
      label: 'PRIMARY',
      addressLine1: '',
      addressLine2: '',
      city: '',
      district: '',
      stateCode: '',
      pincode: '',
      isPrimary: true,
    },
  ]);

  const [banks, setBanks] = useState<BankItem[]>([]);

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(customerFormConfig, createCustomer, {
    routeId: 'customers/new',
    onBeforeSubmit: (value) => {
      // Find primary phone and email from 1-to-many array
      const primaryPhone = contacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || contacts.find((c) => c.type === 'PHONE')?.value || '';
      const altPhone = contacts.filter((c) => c.type === 'PHONE' && c.value !== primaryPhone)[0]?.value || null;
      const primaryEmail = contacts.find((c) => c.type === 'EMAIL' && c.isPrimary)?.value || contacts.find((c) => c.type === 'EMAIL')?.value || null;

      // Client-side phone validation using shared PhoneSchema
      if (!primaryPhone) {
        throw new Error('Primary mobile number is required');
      }
      const phoneResult = PhoneSchema.safeParse(primaryPhone);
      if (!phoneResult.success) {
        throw new Error('Invalid Indian phone number');
      }

      const primaryAddr = addresses.find((a) => a.isPrimary) || addresses[0];
      const hasAddress = primaryAddr && Boolean(primaryAddr.addressLine1?.trim() || primaryAddr.city?.trim() || primaryAddr.pincode?.trim());
      const primaryBank = banks.find((b) => b.isPrimary) || banks[0];

      return {
        name: value.name,
        fatherOrSpouse: value.fatherOrSpouse || null,
        dob: value.dateOfBirth || null,
        gender: value.gender || null,
        mobile: primaryPhone,
        mobileAlt: altPhone,
        email: primaryEmail,
        address: hasAddress && primaryAddr
          ? {
              line1: primaryAddr.addressLine1?.trim() || '',
              line2: primaryAddr.addressLine2?.trim() || null,
              city: primaryAddr.city?.trim() || '',
              district: primaryAddr.district?.trim() || primaryAddr.city?.trim() || '',
              state: primaryAddr.stateCode?.trim() || '',
              pincode: primaryAddr.pincode?.trim() || '',
            }
          : null,
        bankAccountNumber: primaryBank?.accountNumber || null,
        bankIfsc: primaryBank?.ifscCode || null,
        // Pass complete 1-to-many arrays for rich backend relationship persistence
        contacts,
        addresses,
        banks,
      };
    },
    onSuccess: () => {
      clearDraft();
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      queryClient.invalidateQueries({ queryKey: ['crm-leads'] });
      queryClient.invalidateQueries({ queryKey: ['crm-prospects'] });
      queryClient.invalidateQueries({ queryKey: ['crm-converted'] });
      toast({
        type: 'success',
        title: 'Success',
        description: 'Customer created successfully with multi-contact, multi-address, and bank details.',
      });
      void navigate({ to: '/customers' });
    },
    onError: (err: unknown) => {
      toast({ type: 'error', title: 'Error', description: getErrorMessage(toAppError(err)) });
    },
  });

  React.useEffect(() => {
    if (crmContact) {
      if (crmContact.fullName) form.setFieldValue('name', crmContact.fullName);

      // Pre-fill 1-to-many contacts from lead
      const prefilledContacts: ContactItem[] = [];
      if (crmContact.mobile) {
        prefilledContacts.push({ type: 'PHONE', label: 'MOBILE', value: crmContact.mobile, isPrimary: true, isWhatsapp: true });
      }
      if (crmContact.mobileAlt) {
        prefilledContacts.push({ type: 'PHONE', label: 'ALTERNATE', value: crmContact.mobileAlt, isPrimary: false });
      }
      if (crmContact.email) {
        prefilledContacts.push({ type: 'EMAIL', label: 'PERSONAL', value: crmContact.email, isPrimary: true });
      }
      if (prefilledContacts.length > 0) {
        setContacts(prefilledContacts);
      }

      // Pre-fill 1-to-many address from lead
      if (crmContact.addressLine1 || crmContact.city) {
        setAddresses([
          {
            addressType: 'RESIDENTIAL',
            label: 'PRIMARY',
            addressLine1: crmContact.addressLine1 || '',
            city: crmContact.city || '',
            district: crmContact.city || '',
            stateCode: crmContact.state || '',
            pincode: crmContact.pincode || '',
            isPrimary: true,
          },
        ]);
      }
    }
  }, [crmContact, form]);

  const handlePrimaryMobileBlur = async (mobile: string) => {
    if (/^[6-9]\d{9}$/.test(mobile)) {
      try {
        const res = await checkDuplicateMobile(mobile);
        setDuplicateWarning(res.exists ? res : null);
      } catch (err) {
        console.error('Failed to check duplicate mobile', err);
      }
    }
  };

  const primaryMobileVal = contacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || '';

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl">
      {crmContact && (
        <Banner
          variant="info"
          title={`Converting CRM Lead: ${crmContact.fullName || crmContact.name}`}
          description={'Pre-populating customer profile from lead (' + crmContact.mobile + ').'}
        />
      )}

      {hasDraft && (
        <Banner
          variant="info"
          title="Restore Draft"
          description="You have an unsaved draft from a previous session. Would you like to restore it?"
          action={{
            label: 'Restore',
            onClick: () => {
              const draft = restoreDraft();
              if (draft) {
                Object.entries(draft).forEach(([key, val]) => {
                  form.setFieldValue(key as never, val as never);
                });
              }
              dismissDraft();
            },
          }}
          secondaryAction={{
            label: 'Discard',
            onClick: () => {
              clearDraft();
            },
          }}
        />
      )}

      {/* Section 1: Personal Details */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium border-b pb-2 text-gray-900">Personal Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <form.Field
            name="name"
            validators={{ onChange: fieldErr(customerSchema.shape.name) }}
            children={(field: AnyFieldApi) => (
              <FormField
                label="Full Name"
                required
                htmlFor="name"
                error={showErr(form, field) ? (field.state.meta.errors[0] as string | undefined) : undefined}
              >
                <Input
                  id="name"
                  autoComplete="name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                  error={showErr(form, field)}
                />
              </FormField>
            )}
          />
          <form.Field
            name="fatherOrSpouse"
            children={(field: AnyFieldApi) => (
              <FormField label="Father / Spouse Name" htmlFor="fatherOrSpouse">
                <Input
                  id="fatherOrSpouse"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />
          <form.Field
            name="dateOfBirth"
            validators={{ onChange: fieldErr(customerSchema.shape.dateOfBirth) }}
            children={(field: AnyFieldApi) => (
              <FormField
                label="Date of Birth"
                htmlFor="dateOfBirth"
                error={showErr(form, field) ? (field.state.meta.errors[0] as string | undefined) : undefined}
              >
                <Input
                  id="dateOfBirth"
                  type="date"
                  autoComplete="bday"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                  error={showErr(form, field)}
                />
              </FormField>
            )}
          />
          <form.Field
            name="gender"
            children={(field: AnyFieldApi) => (
              <FormSelect
                label="Gender"
                options={[
                  { value: '', label: 'Select gender' },
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' },
                ]}
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  field.handleChange(e.target.value as 'male' | 'female' | 'other' | '')
                }
                disabled={isSubmitting}
              />
            )}
          />
        </div>
      </div>

      {/* Section 2: Dynamic 1-to-Many Phone Numbers & Emails */}
      <div className="border-t pt-6">
        <DynamicContactList
          contacts={contacts}
          disabled={isSubmitting}
          onChange={(newContacts) => {
            setContacts(newContacts);
            const primaryPhone = newContacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || '';
            if (primaryPhone && primaryPhone !== primaryMobileVal) {
              void handlePrimaryMobileBlur(primaryPhone);
            }
          }}
        />
        {duplicateWarning?.exists && duplicateWarning.customerId && (
          <p className="text-sm text-warning-600 font-medium mt-2">
            Primary mobile already in use.{' '}
            <Link to={`/customers/$id`} params={{ id: duplicateWarning.customerId }} className="underline">
              View existing customer profile
            </Link>
          </p>
        )}
      </div>

      {/* Section 3: Dynamic 1-to-Many Addresses */}
      <div className="border-t pt-6">
        <DynamicAddressList
          addresses={addresses}
          disabled={isSubmitting}
          onChange={(newAddresses) => setAddresses(newAddresses)}
        />
      </div>

      {/* Section 4: Dynamic 1-to-Many Bank Accounts & VPAs */}
      <div className="border-t pt-6">
        <DynamicBankList
          banks={banks}
          disabled={isSubmitting}
          onChange={(newBanks) => setBanks(newBanks)}
        />
      </div>

      {/* Form Action Buttons */}
      <div className="flex justify-end gap-4 border-t pt-6">
        <Button variant="secondary" type="button" disabled={isSubmitting} onClick={() => void navigate({ to: '/customers' })}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={isSubmitting}>
          Create Customer
        </Button>
      </div>
    </form>
  );
}
